import { tryMockLead } from './devMockLead'

const API_URL = import.meta.env.VITE_API_URL || ''

export class ApiError extends Error {
  statusCode: number
  code: string

  constructor(response: Response, body?: { error?: { code?: string; message?: string } }) {
    super(body?.error?.message || `API error: ${response.status}`)
    this.statusCode = response.status
    this.code = body?.error?.code || 'UNKNOWN_ERROR'
  }
}

// Refresh dedup: concurrent 401s share one refresh request
let refreshPromise: Promise<boolean> | null = null

async function doRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
    return res.ok
  } catch {
    return false
  }
}

function refreshToken(): Promise<boolean> {
  if (refreshPromise) return refreshPromise
  refreshPromise = doRefresh().finally(() => {
    refreshPromise = null
  })
  return refreshPromise
}

/**
 * Core HTTP client. Used by RTK Query baseQuery.
 * Returns { data } on success or { error } on failure.
 * On 401: auto-refresh once, retry. If refresh fails, returns AUTH_EXPIRED error
 * (caller handles redirect via React Router, NOT window.location).
 *
 * All requests have a 30s timeout to prevent indefinite hangs on slow/dead backends.
 */
export async function apiClient<T = unknown>(
  path: string,
  options?: RequestInit & { _isRetry?: boolean },
): Promise<{ data: T } | { error: { status: number; code: string; message: string } }> {
  const headers: Record<string, string> = {}
  if (options?.headers) {
    const h = options.headers
    if (h instanceof Headers) {
      h.forEach((v, k) => { headers[k] = v })
    } else if (Array.isArray(h)) {
      h.forEach(([k, v]) => { headers[k] = v })
    } else {
      Object.assign(headers, h)
    }
  }

  if (options?.body && !(options.body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] ?? 'application/json'
  }

  let res: Response
  try {
    // Dev-only мок лида: перехватываем fetch если активирован тестовый сценарий.
    // В production-сборке этот блок tree-shake'ится через `import.meta.env.DEV`.
    const mockRes = import.meta.env.DEV
      ? tryMockLead(path, { ...options, headers })
      : null
    if (mockRes) {
      res = mockRes
    } else {
      // 30s timeout to prevent indefinite hangs
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 30000)

      try {
        res = await fetch(`${API_URL}${path}`, {
          ...options,
          credentials: 'include',
          headers,
          signal: controller.signal,
        })
      } finally {
        clearTimeout(timeoutId)
      }
    }
  } catch (e) {
    // Handle AbortError (timeout) separately from generic network errors
    const isAborted = e instanceof DOMException && e.name === 'AbortError'
    const message = isAborted ? 'Запрос занял слишком долго' : 'Сервер недоступен'
    const code = isAborted ? 'TIMEOUT' : 'NETWORK_ERROR'
    return { error: { status: 0, code, message } }
  }

  if (res.status === 401 && !options?._isRetry) {
    // Don't auto-refresh for auth endpoints (login, register) — 401 means wrong credentials.
    // Use exact match (not startsWith) to avoid false positives like /api/auth/contract-list.
    const pathOnly = path.split('?')[0]
    const AUTH_ENDPOINTS = new Set([
      '/api/auth/contract',
      '/api/auth/register',
      '/api/auth/call-verify',
      '/api/auth/call-verify/request',
      '/api/auth/logout',
    ])
    const isAuthEndpoint = AUTH_ENDPOINTS.has(pathOnly)
    if (!isAuthEndpoint) {
      const refreshed = await refreshToken()
      if (refreshed) {
        return apiClient<T>(path, { ...options, _isRetry: true })
      }
      return { error: { status: 401, code: 'AUTH_EXPIRED', message: 'Сессия истекла' } }
    }
    // For auth endpoints, fall through to generic error handling below
  }

  if (!res.ok) {
    let body: { error?: { code?: string; message?: string } } | undefined
    try {
      body = await res.json()
    } catch { /* empty */ }
    return {
      error: {
        status: res.status,
        code: body?.error?.code || 'UNKNOWN_ERROR',
        message: body?.error?.message || `Ошибка сервера: ${res.status}`,
      },
    }
  }

  const text = await res.text()
  if (!text) return { data: undefined as T }
  return { data: JSON.parse(text) as T }
}
